import { useEffect, useReducer, useRef } from 'react'

import { GenerationStatus } from '../types/model'
import type { GeneratedContent } from '../types/domain'

export interface FetchState<T> {
  isLoading: boolean
  isGenerating: boolean
  data?: T
  error: boolean
}

export enum FetchActionType {
  LOADING = 'loading',
  GENERATING = 'generating',
  SUCCESS = 'success',
  ERROR = 'error',
  RESET = 'reset',
}

interface Config {
  errorOnNull?: boolean
  // PAYPER-1123: el endpoint devuelve { status, data } y "processing" debe
  // mostrarse como estado propio, distinto del error y del loading inicial.
  hasGenerationStatus?: boolean
  // PAYPER-1181: la primera consulta dispara la generación en el back (202).
  // Si se configura, se reintenta cada `intervalMs` hasta `maxRetries` veces.
  retryOnProcessing?: { intervalMs: number; maxRetries: number }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const useFetchReducer = <T>(
  fetchFn: (...args: any[]) => Promise<T | GeneratedContent<T>>,
  config: Config = { errorOnNull: true },
): [FetchState<T>, (...args: any[]) => Promise<void>, () => void] => {
  const initialState: FetchState<T> = {
    isLoading: false,
    isGenerating: false,
    data: undefined,
    error: false,
  }

  const reducer = (
    state: FetchState<T>,
    action: { type: FetchActionType; payload?: T },
  ) => {
    switch (action.type) {
      case FetchActionType.LOADING:
        return { ...initialState, isLoading: true }
      case FetchActionType.GENERATING:
        return { ...initialState, isGenerating: true }
      case FetchActionType.SUCCESS:
        return { ...initialState, isLoading: false, data: action.payload }
      case FetchActionType.ERROR:
        return { ...initialState, isLoading: false, error: true }
      case FetchActionType.RESET:
        return initialState
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  // PAYPER-1181: evita lanzar dos bucles de reintento a la vez (un nuevo clic
  // mientras se genera) y corta el bucle si el componente se desmonta.
  const isFetchingRef = useRef(false)
  const isUnmountedRef = useRef(false)

  useEffect(() => {
    isUnmountedRef.current = false
    return () => {
      isUnmountedRef.current = true
    }
  }, [])

  const handleGeneratedContent = async (
    firstResponse: GeneratedContent<T>,
    args: any[],
  ) => {
    let response = firstResponse
    const maxRetries = config.retryOnProcessing?.maxRetries ?? 0
    let retries = 0

    while (response.status === GenerationStatus.PROCESSING) {
      if (retries >= maxRetries) {
        dispatch({
          type: config.retryOnProcessing
            ? FetchActionType.ERROR
            : FetchActionType.GENERATING,
        })
        return
      }
      if (retries === 0) {
        dispatch({ type: FetchActionType.GENERATING })
      }
      await wait(config.retryOnProcessing!.intervalMs)
      if (isUnmountedRef.current) return
      response = (await fetchFn(...args)) as GeneratedContent<T>
      if (isUnmountedRef.current) return
      retries++
    }

    if (response.status === GenerationStatus.ERROR || response.data === null) {
      dispatch({ type: FetchActionType.ERROR })
    } else {
      dispatch({ type: FetchActionType.SUCCESS, payload: response.data as T })
    }
  }

  const handleFetch = async (...args: any[]) => {
    if (isFetchingRef.current) return
    isFetchingRef.current = true
    dispatch({ type: FetchActionType.LOADING })
    try {
      const response = await fetchFn(...args)
      if (isUnmountedRef.current) return

      if (config.hasGenerationStatus) {
        await handleGeneratedContent(response as GeneratedContent<T>, args)
        return
      }

      if (config.errorOnNull && response === null) {
        dispatch({ type: FetchActionType.ERROR })
      } else {
        dispatch({ type: FetchActionType.SUCCESS, payload: response as T })
      }
    } catch (error) {
      if (!isUnmountedRef.current) dispatch({ type: FetchActionType.ERROR })
    } finally {
      isFetchingRef.current = false
    }
  }

  const resetState = () => {
    dispatch({ type: FetchActionType.RESET })
  }

  return [state, handleFetch, resetState]
}

export default useFetchReducer
